import theme from '@assets/theme';
import {
  useFetchLicenseDataAndSetStoreValues,
  useFetchWhoami,
} from '@http/queries';
import { useMyEffectiveRights } from '@http/queries/useMyEffectiveRights';
import { ThemeProvider, CssBaseline } from '@mui/material';
import * as Sentry from '@sentry/browser';
import useWindowSize from '@hooks/useWindowSize';
import { useQueryClient } from '@tanstack/react-query';
import 'chartjs-adapter-date-fns';
import { fromUnixTime } from 'date-fns';
import { useAtom, useSetAtom } from 'jotai';
import { Fragment, useEffect, useState } from 'react';
import {
  Navigate,
  Outlet,
  useMatches,
  useLocation,
  ScrollRestoration,
} from 'react-router-dom';
import { useBootScreen } from '@/hooks/useBootScreen';
import {
  demoModeAtom,
  effectiveAccessRightsAtom,
  rerenderLayoutAtom,
  tokenExpiresAtom,
  userAtom,
} from './global-store';
import clearStorageWithExeptions from './helpers/clearStorageWithExeptions';
import { resolveRouteAccessRedirect } from '@/guards/routeAccess';
import { MainLayout } from '@layout/MainLayout';
import { usePathname } from '@/hooks/usePathname';
import PrivacyDialog from './pages/authentication/PrivacyDialog';
import { SidebarProvider } from '@layout/Sidebar/Sidebar';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { usePostHog } from '@posthog/react';
import {
  disablePostHogTracking,
  enablePostHogTracking,
  isPostHogTrackingEnabled,
} from '@/posthogClient';
import { Notification } from '@eye-able/ui';
import { useApplyBrandButtonVars } from '@/branding/useOrgBranding';

function BaseRoute() {
  const posthog = usePostHog();
  const { t, i18n, ready: i18nReady } = useInlineTranslation();

  // Re-colours the shared yellow CTA button to the org brand colour, app-wide.
  useApplyBrandButtonVars();

  const [isLoading, setIsLoading] = useState(true);
  const [langSetup, setLangSetup] = useState(false);
  const [renderKey, setRenderKey] = useAtom(rerenderLayoutAtom);

  const queryClient = useQueryClient();

  const { isLoading: LicenseDataLoading, isError: LicenseDataError } =
    useFetchLicenseDataAndSetStoreValues();
  const { isLoading: whoamiLoading, isError: whoamiError } = useFetchWhoami();
  // Current user's viewer/editor access levels. Intentionally NOT part of the
  // boot-readiness gate: edit gating is a UX nicety and the backend stays the
  // source of truth, so a slow/failed fetch must never block the app.
  const { data: myEffectiveRights } = useMyEffectiveRights();
  const setEffectiveAccessRights = useSetAtom(effectiveAccessRightsAtom);

  const [user, setUser] = useAtom(userAtom);
  const [tokenExpires, setTokenExpires] = useAtom(tokenExpiresAtom);
  const [demoMode] = useAtom(demoModeAtom);

  const matches = useMatches();
  const location = useLocation();
  const path = usePathname();
  const { enableBootScreen, disableBootScreen } = useBootScreen();

  const windowsSize = useWindowSize().width;
  const isMobileOrTablet = windowsSize < 1180;
  const isMobile = windowsSize < 580;
  const isUserReady =
    !whoamiLoading && !whoamiError && !LicenseDataLoading && !!user;

  useEffect(() => {
    if (whoamiError || LicenseDataError) {
      if (['privacy-policy', 'authentication'].includes(path.split('/')[1]))
        return setIsLoading(false);
      else {
        disablePostHogTracking({ reset: true });
        Sentry.setUser(undefined);
        setUser(undefined);
        clearStorageWithExeptions();
        window.location.replace('/authentication/login');
      }
    }
  }, [whoamiError, setUser, LicenseDataError, path]);

  useEffect(() => {
    if (isUserReady) return;

    disablePostHogTracking();
  }, [isUserReady]);

  useEffect(() => {
    if (!isUserReady) return;

    enablePostHogTracking();

    if (isPostHogTrackingEnabled()) {
      posthog.identify(user.id);
      posthog.setPersonProperties({
        organization: user.company,
        organization_id: user.primaryOrganisation,
        role: user.role,
        is_customer: user.isCustomer,
      });
    }

    Sentry.setUser({
      id: user.id,
      username: user.firstName?.[0] + '.' + user.lastName?.[0],
    });

    setIsLoading(false);
  }, [posthog, isUserReady, user]);

  useEffect(() => {
    if (isUserReady) {
      posthog.capture('$pageview');
    }
  }, [posthog, isUserReady, path]);

  useEffect(() => {
    // Clear stale rights on logout/user change so the UI never gates edits
    // based on the previous user's access while the new fetch is pending.
    if (!user || myEffectiveRights?.userId !== user.id) {
      setEffectiveAccessRights(null);
      return;
    }
    setEffectiveAccessRights(myEffectiveRights.effectiveRights ?? null);
  }, [myEffectiveRights, user, setEffectiveAccessRights]);

  useEffect(() => {
    if (i18nReady) {
      setLangSetup(true);
    }
  }, [i18nReady, i18n.resolvedLanguage, setLangSetup]);

  useEffect(() => {
    if (langSetup && !isLoading) {
      disableBootScreen();
    } else {
      enableBootScreen();
    }
  }, [
    disableBootScreen,
    isLoading,
    demoMode,
    user,
    path,
    i18n.resolvedLanguage,
    queryClient,
    langSetup,
    enableBootScreen,
  ]);

  useEffect(() => {
    if (!tokenExpires || !user) return;
    // show an alert before the token expires
    // TODO: show a dialog with relogin option
    const now = new Date();

    const expires = Number(tokenExpires);

    const expiresInSeconds = expires - (now.getTime() / 1000).toFixed(0);

    const expiresInMs = expiresInSeconds * 1000;

    const expireDate = fromUnixTime(Number(tokenExpires));

    // check if the token will expires in less than 24 days, which is the max value for setTimeout
    if (expiresInMs < 2147480000) {
      // logout the user when the token expires
      setTimeout(() => {
        setUser(undefined);
        setTokenExpires(undefined);
        alert(
          'Your session has expired, please login again on the following page',
        );
        window.location.replace('/authentication/login');
      }, expiresInMs);

      // show an alert 10 minutes before the token expires
      // TODO: show a dialog with relogin option
      setTimeout(() => {
        console.log('Token will expire in 10 minutes');
        // Set the minutes to 10 minutes before the token expires
        expireDate.setMinutes(expireDate.getMinutes() - 10);

        // Get hours and minutes, adding a leading zero if necessary
        const hours = expireDate.getHours().toString().padStart(2, '0');
        const minutes = expireDate.getMinutes().toString().padStart(2, '0');

        alert(
          `Your Session will expire in 10 minutes (at ${hours}:${minutes}), please logout and login again`,
        );
      }, expiresInMs - 6000);
    }
  }, [tokenExpires, user, setUser, setTokenExpires]);

  // Reload page if localStorage changes from another tab
  useEffect(() => {
    function reloadPage(event) {
      if (!document.hidden) return; // check if the tab is hidden

      const { key, oldValue, newValue } = event;

      if (
        ['reload', 'user', 'demoMode', 'X-Dev-Token', 'i18nextLng'].includes(
          key,
        ) &&
        newValue !== oldValue
      ) {
        window.location.reload();
      }
    }

    // this event only fires when the localStorage is changed from another tab
    window.addEventListener('storage', reloadPage);

    return () => {
      window.removeEventListener('storage', reloadPage);
    };
  }, [setRenderKey]);

  useEffect(() => {
    // add padding so that the footer is not covered by the contact buttons
    document.getElementById('app').style.paddingBottom =
      isMobileOrTablet && demoMode ? (isMobile ? '64px' : '32px') : 0;
  }, [isMobileOrTablet, demoMode, isMobile]);

  if (!langSetup || isLoading) {
    return null;
  }

  if (path === '/report') {
    return <Navigate replace to={`/report/overview${location.search}`} />;
  }

  if (
    user &&
    !user.hasPassword &&
    path !== '/authentication/change-password' &&
    !demoMode &&
    !window.location.origin.includes('admin')
  ) {
    return window.location.assign('/authentication/change-password');
  } else {
    const redirectPath = resolveRouteAccessRedirect(user, matches, demoMode);

    if (redirectPath && path !== redirectPath) {
      return window.location.assign(redirectPath);
    }
  }

  return (
    <ThemeProvider theme={theme} key={i18n.resolvedLanguage}>
      <CssBaseline />

      <ScrollRestoration />

      <Fragment key={renderKey}>
        {!user || path.startsWith('/authentication/') ? (
          <Outlet />
        ) : (
          <>
            <SidebarProvider>
              <MainLayout />
            </SidebarProvider>
          </>
        )}
      </Fragment>
      {path.includes('privacy-policy') ? null : <PrivacyDialog />}

      <Notification
        toastOptions={{
          closeButtonAriaLabel: t('close'),
        }}
        aria-live='polite'
      />
    </ThemeProvider>
  );
}

export default BaseRoute;
